/**
 * Canonical Axiom event shape shared by every ingest path.
 *
 * Axiom datasets have a hard cap on distinct field names (256 on the free
 * plan), and every top-level key — including each key of a nested object,
 * which Axiom flattens to `parent.child` — permanently occupies one slot.
 * All event-specific keys therefore live under `data`, which a project
 * configures as a **map field** on its dataset so the nested keys never
 * count toward the limit. See logging-strategy POLICY.md §12.
 *
 * The name is Axiom's, but the shape is not: a non-Axiom sink is just
 * another `LogBackend` consuming this same canonical event, and `_time` is
 * the only field Axiom itself contributes.
 *
 * Rules:
 * - Never spread an entry (or any arbitrary object) into the root of an
 *   event — every new key becomes a dataset field forever.
 * - Adding a top-level field is a deliberate schema decision: add it here
 *   and to `BaseLogEntry`, nowhere else.
 * - Every ingest path MUST emit this exact shape so queries
 *   (`userId == …`, `['data']['duration_ms']`) behave identically
 *   regardless of where the event originated.
 */

import type { BaseLogEntry, LogError, LogLevel } from "./types";

/**
 * The event object ingested into the sink. Field-for-field this is
 * `BaseLogEntry` plus Axiom's `_time`; it exists as its own type so the
 * top-level field budget is explicit and closed.
 */
export interface AxiomEvent {
  /** Axiom's canonical timestamp field (ISO 8601). */
  _time: string;
  level: LogLevel;
  eventName: string;
  eventId: string;
  timestamp: number;
  environment: string;
  service: string;
  version?: string;
  sessionId?: string;
  userId?: string;
  readerId?: string;
  leadId?: string;
  /** Event-specific payload — stored as an Axiom map field. */
  data?: Record<string, unknown>;
  /**
   * Fixed three-key error object; safe as regular (non-map) fields.
   * Enumerated key-by-key in `toAxiomEvent` — extra keys on a caller's
   * `LogError` must not leak into the dataset schema.
   */
  error?: LogError;
}

/**
 * Convert a log entry to the canonical event.
 *
 * Fields are enumerated explicitly (no spread) so that a future addition
 * to `BaseLogEntry` cannot silently create a new dataset field.
 */
export function toAxiomEvent(entry: BaseLogEntry): AxiomEvent {
  return {
    _time: new Date(entry.timestamp).toISOString(),
    level: entry.level,
    eventName: entry.eventName,
    eventId: entry.eventId,
    timestamp: entry.timestamp,
    environment: entry.environment,
    service: entry.service,
    version: entry.version,
    sessionId: entry.sessionId,
    userId: entry.userId,
    readerId: entry.readerId,
    leadId: entry.leadId,
    data: entry.data,
    error: entry.error
      ? {
          type: entry.error.type,
          message: entry.error.message,
          code: entry.error.code,
        }
      : undefined,
  };
}
